import { Injectable } from '@angular/core';
import { List } from 'immutable';
import { Observable } from 'rxjs';
import {
  ACCEPT_VERSION_HEADER,
  AuthHttpService,
} from '../auth-data/auth-http.service';
import { CreditMemoDataBuilder } from '../credit-memo-data/credit-memo-data.builder';
import { DocumentTypesEnum } from '../document-data/document-data.data';
import { Document } from '../document-data/document.model';
import {
  IEntityData,
  ISearchParams,
  ITopLevelKeys,
  NEXT_PAGE_HEADER,
} from '../entity-data/entity-data.data';
import { EntityDataService } from '../entity-data/entity-data.service';
import { ErrorService } from '../error-reporting/error.service';
import { EstimateDataBuilder } from '../estimate-data/estimate-data.builder';
import { IResponseInfo } from '../http/http.data';
import { InvoiceDataBuilder } from '../invoice-data/invoice-data.builder';
import { PurchaseOrderDataBuilder } from '../purchase-order-data/purchase-order-data.builder';
import { GlobalSearch } from '../search-data/global-search.model';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { Settings } from '../settings-data/settings.model';
import { LoggerService } from '../shared/services/logger/logger.service';
import { ClientDataBuilder } from './client-data.builder';
import {
  CLIENT_ACCEPT_VERSION,
  CLIENT_DOCUMENTS_ACCEPT_VERSION,
  CLIENT_SEARCH_ACCEPT_VERSION,
  DocumentHistoryFilterTypesEnum,
} from './client-data.data';
import { identity, pickBy } from 'lodash';
import { Client } from './client.model';
import { map } from 'rxjs/operators';

@Injectable()
export class ClientDataService extends EntityDataService {
  protected inputHeaders: any = {
    [ACCEPT_VERSION_HEADER]: CLIENT_ACCEPT_VERSION,
  };
  protected searchHeaders: any = {
    [ACCEPT_VERSION_HEADER]: CLIENT_SEARCH_ACCEPT_VERSION,
  };

  private _documentHeaders: any = {
    [ACCEPT_VERSION_HEADER]: CLIENT_DOCUMENTS_ACCEPT_VERSION,
  };

  topLevelKeys: ITopLevelKeys = {
    getKey: 'clients',
    postKey: 'created_client',
    putKey: 'updated_client',
    deletedKey: 'deleted_clients',
    deleteSingleKey: 'deleted_client',
    getSingleKey: 'client',
    searchResultsKey: 'client_results',
    globalSearchResultsKey: GlobalSearch.KEYS.CLIENTS,
  };

  constructor(
    private _invoiceDataBuilder: InvoiceDataBuilder,
    private _estimateDataBuilder: EstimateDataBuilder,
    private _creditMemoDataBuilder: CreditMemoDataBuilder,
    private _purchaseOrderDataBuilder: PurchaseOrderDataBuilder,
    private _clientDataBuilder: ClientDataBuilder,
    authHttpService: AuthHttpService,
    loggerService: LoggerService,
    errorService: ErrorService,
    settingsDataStore: SettingsDataStore
  ) {
    super(
      authHttpService,
      '/clients',
      loggerService,
      errorService,
      _clientDataBuilder,
      settingsDataStore
    );
  }

  isUniqueClient(client: Client): Observable<boolean> {
    return this.searchEntities(
      this._clientDataBuilder.buildUniquenessCheckSearchParams(client)
    ).pipe(
      map(({ entities }: IEntityData) => {
        const filteredSearchResults = entities[0].filter((client2: Client) => {
          return (
            (client.billingName || '') === (client2.billingName || '') &&
            (client.contactName || '') === (client2.contactName || '') &&
            (client.email || '') === (client2.email || '')
          );
        });

        return filteredSearchResults.size === 0;
      })
    );
  }

  getClientDocuments(
    clientId: string,
    filterType: DocumentHistoryFilterTypesEnum,
    searchParams: ISearchParams,
    settings: Settings
  ): Observable<IEntityData> {
    let path: string;
    let params: any = {
      ...searchParams,
      list_category: null,
    };

    switch (filterType) {
      case DocumentHistoryFilterTypesEnum.all_documents:
        path = 'documents';
        break;
      case DocumentHistoryFilterTypesEnum.invoices:
        path = 'invoices';
        break;
      case DocumentHistoryFilterTypesEnum.estimates:
        path = 'estimates';
        break;
      case DocumentHistoryFilterTypesEnum.credit_memos:
        path = 'credit_memos';
        break;
      case DocumentHistoryFilterTypesEnum.purchase_orders:
        path = 'purchase_orders';
        break;
      case DocumentHistoryFilterTypesEnum.unpaid:
        path = 'invoices';
        params = {
          ...params,
          list_category: 'unpaid',
        };
        break;
      case DocumentHistoryFilterTypesEnum.overdue:
        path = 'invoices';
        params = {
          ...params,
          list_category: 'overdue',
        };
        break;
    }

    return this.authHttpService
      .get(
        `${this.entitiesUrl}/${clientId}/${path}`,
        pickBy(params, identity),
        this._documentHeaders
      )
      .pipe(
        map(({ body, headers }: IResponseInfo) => {
          const nextPageHeader = headers.get(NEXT_PAGE_HEADER);
          const searchResultParams = {
            ...params,
            next_page_url: nextPageHeader,
          };
          const entities = List(body['client_documents']['documents']).map(
            (props: any) => {
              const doc = new Document(props);

              switch (doc.documentType) {
                case DocumentTypesEnum.invoice:
                  return this._invoiceDataBuilder.buildEntity(doc, settings);
                case DocumentTypesEnum.estimate:
                  return this._estimateDataBuilder.buildEntity(doc, settings);
                case DocumentTypesEnum.credit_memo:
                  return this._creditMemoDataBuilder.buildEntity(doc, settings);
                case DocumentTypesEnum.purchase_order:
                  return this._purchaseOrderDataBuilder.buildEntity(
                    doc,
                    settings
                  );
              }
            }
          ) as List<Document>;

          return {
            entities: [entities],
            headers,
            searchParams: searchResultParams,
          };
        })
      );
  }

  createClientBankDetails(clientId: string) {
    return this.authHttpService
      .post(
        `${this.entitiesUrl}/${clientId}/ach_credit_bank_details`,
        {},
        this.inputHeaders
      )
      .pipe(map((response) => new Client(response.body.client)));
  }

  sendBankDetailsToEmail(
    clientId: string,
    email: string
  ): Observable<IResponseInfo> {
    const url = `${this.entitiesUrl}/${clientId}/ach_credit_bank_details/email`;
    const body = {
      email,
    };

    return this.authHttpService.post(url, body, this.inputHeaders);
  }
}
